// The surveillance feed — Instagram-style stream of every agent's proofs,
// with an inline upload row. Embedded in HQ's FEED tab (and the /gallery
// deep link). Most photos arrive via cache claims; this is the social wall.
import { el, toast } from "../lib/dom";
import { db } from "../lib/firebase";
import {
  collection, addDoc, onSnapshot, query, orderBy, serverTimestamp,
} from "firebase/firestore";
import { compressImage, uploadSelfie } from "../lib/upload";
import { noirProof } from "../lib/avatar-filter";
import { watchGuest } from "../lib/data";

interface Photo {
  id: string;
  guestId: string;
  codename?: string;
  downloadURL: string;
  caption?: string;
  type?: string;
  createdAt?: { toMillis(): number };
}

function ago(ms?: number): string {
  if (!ms) return "…";
  const s = Math.max(1, Math.round((Date.now() - ms) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return new Date(ms).toLocaleDateString();
}

export function photoFeed(uid: string): { el: HTMLElement; cleanup: () => void } {
  let codename = "AGENT";
  const unsubs: Array<() => void> = [];

  // ---- upload row ----
  const fileInput = el("input", {
    type: "file", accept: "image/*", capture: "user", style: "display:none",
  }) as HTMLInputElement;
  const captionInput = el("input.input", { placeholder: "Caption this intel…" }) as HTMLInputElement;
  const shootBtn = el("button.btn.btn--sm", { style: "flex:none" }, "📷 File proof") as HTMLButtonElement;

  shootBtn.addEventListener("click", () => fileInput.click());
  fileInput.addEventListener("change", async () => {
    const file = fileInput.files?.[0];
    if (!file) return;
    shootBtn.disabled = true;
    shootBtn.textContent = "Encrypting…";
    try {
      const blob = await noirProof(await compressImage(file));
      const { path, url } = await uploadSelfie(uid, blob);
      await addDoc(collection(db, "photos"), {
        guestId: uid,
        codename,
        storagePath: path,
        downloadURL: url,
        caption: captionInput.value.trim(),
        createdAt: serverTimestamp(),
      });
      toast("Proof filed. Nice work.", "ok");
      fileInput.value = "";
      captionInput.value = "";
    } catch (e: unknown) {
      toast((e as { message?: string })?.message ?? "Upload failed.", "err");
    } finally {
      shootBtn.disabled = false;
      shootBtn.textContent = "📷 File proof";
    }
  });

  const uploadRow = el("div.card", {},
    el("div.row", { style: "gap:8px" }, captionInput, shootBtn),
    fileInput,
  );

  // ---- stream ----
  const stream = el("div");
  unsubs.push(watchGuest(uid, (g) => { codename = g?.displayName ?? "AGENT"; }));
  unsubs.push(onSnapshot(
    query(collection(db, "photos"), orderBy("createdAt", "desc")),
    (snap) => {
      const photos = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Photo, "id">) }));
      stream.replaceChildren(
        ...(photos.length
          ? photos.map(feedCard)
          : [el("p.tiny.dim.center", {}, "No proof filed yet. The wall is watching.")]),
      );
    },
  ));

  function feedCard(p: Photo): HTMLElement {
    if (p.type === "agentJoined") {
      return el("div.card.feed-card", {
        style: "border-color:var(--amber);text-align:center",
      },
        el("div.row", { style: "justify-content:space-between;margin-bottom:12px" },
          el("span.card__tag", { style: "border-color:var(--amber);color:var(--amber)" }, "AGENT ACTIVE"),
          el("span.tiny.dim", {}, ago(p.createdAt?.toMillis?.())),
        ),
        el("img", {
          src: p.downloadURL,
          loading: "lazy",
          style: "width:96px;height:96px;border-radius:50%;object-fit:cover;border:2px solid var(--amber);display:inline-block",
        }),
        el("div.mono.amber", { style: "font-weight:700;margin-top:8px;font-size:15px" }, p.codename ?? "AGENT"),
        el("p.tiny.dim", { style: "margin:4px 0 0" }, "Cover identity confirmed."),
      );
    }
    return el("div.card.feed-card", {},
      el("div.row", { style: "justify-content:space-between;margin-bottom:8px" },
        el("span.mono.amber", { style: "font-weight:700" }, p.codename ?? "AGENT"),
        el("span.tiny.dim", {}, ago(p.createdAt?.toMillis?.())),
      ),
      el("img.feed-card__img", { src: p.downloadURL, loading: "lazy" }),
      p.caption ? el("p", { style: "margin:8px 0 0" }, p.caption) : null,
    );
  }

  return {
    el: el("div", {}, uploadRow, stream),
    cleanup: () => unsubs.forEach((u) => u()),
  };
}
